import { AdsAssistantFeedImportFields } from "@/components/ads-assistant/AdsAssistantFeedImportFields";
import { AdsAssistantFeedImportGooglePanel } from "@/components/ads-assistant/AdsAssistantFeedImportGooglePanel";
import { AdsAssistantFeedImportMetaPanel } from "@/components/ads-assistant/AdsAssistantFeedImportMetaPanel";
import { AdsAssistantFeedImportStatusBanner } from "@/components/ads-assistant/AdsAssistantFeedImportStatusBanner";
import { useAdsAssistantFeedImport } from "@/components/ads-assistant/useAdsAssistantFeedImport";

type Props = { scopedOrgId: string | null; embedded?: boolean; /** Sales lane: CRM sync only (legacy; prefer AdsAssistantIngestCrmSyncPanel). */ salesGhlOnly?: boolean };

export function AdsAssistantFeedImportSection({ scopedOrgId, embedded, salesGhlOnly }: Props) {
  const vm = useAdsAssistantFeedImport(scopedOrgId, { salesGhlOnly });

  const title = salesGhlOnly ? "Sync ads from CRM" : "Meta / Google feed import";

  if (!scopedOrgId) {
    return embedded ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Choose an organization to use feed import.</p>
    ) : (
      <section
        className="rounded-2xl border border-indigo-200/80 bg-indigo-50/30 px-4 py-3 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-950/20"
        aria-labelledby="ads-feed-import-heading"
      >
        <h3 id="ads-feed-import-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Meta / Google feed import
        </h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Choose an organization to use feed import.</p>
      </section>
    );
  }

  const shell = embedded
    ? "space-y-3"
    : "rounded-2xl border border-indigo-200/80 bg-indigo-50/30 px-4 py-3 shadow-sm dark:border-indigo-900/60 dark:bg-indigo-950/20";

  return (
    <section className={shell} aria-labelledby={embedded ? undefined : "ads-feed-import-heading"}>
      {!embedded ? (
        <>
          <h3 id="ads-feed-import-heading" className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 max-w-prose">
            {salesGhlOnly
              ? "Pull published Facebook and Google posts for your assigned location. Credit goes to your lot’s sales rep when configured."
              : "Preferred: sync published Facebook and Google posts from CRM Social Planner (uses the org CRM Private Integration in Admin). Advanced: Meta Graph fetch or paste JSON. Posts upsert into the same index as manual entries; repeat syncs skip unchanged rows."}
          </p>
        </>
      ) : null}

      <AdsAssistantFeedImportStatusBanner scopedOrgId={scopedOrgId} showVaultHints={!salesGhlOnly} />

      {!salesGhlOnly ? (
      <AdsAssistantFeedImportMetaPanel
        embedded={embedded}
        mode={vm.mode}
        metaLoading={vm.metaLoading}
        metaConfigured={vm.metaConfigured}
        metaUpdatedAt={vm.metaUpdatedAt}
        encryptionConfigured={vm.encryptionConfigured}
        canEditMetaToken={vm.canEditMetaToken}
        saveTokenDraft={vm.saveTokenDraft}
        setSaveTokenDraft={vm.setSaveTokenDraft}
        saveVaultBusy={vm.saveVaultBusy}
        metaBanner={vm.metaBanner}
        saveOrgToken={vm.saveOrgToken}
        clearOrgToken={vm.clearOrgToken}
      />
      ) : null}

      {!salesGhlOnly ? (
      <AdsAssistantFeedImportGooglePanel
        embedded={embedded}
        mode={vm.mode}
        googleLoading={vm.googleLoading}
        googleConfigured={vm.googleConfigured}
        googleUpdatedAt={vm.googleUpdatedAt}
        googleEncryptionConfigured={vm.googleEncryptionConfigured}
        canEditGoogleCredentials={vm.canEditGoogleCredentials}
        saveCredentialsDraft={vm.saveCredentialsDraft}
        setSaveCredentialsDraft={vm.setSaveCredentialsDraft}
        saveVaultBusy={vm.saveVaultBusy}
        googleBanner={vm.googleBanner}
        saveOrgCredentials={vm.saveOrgGoogleCredentials}
        clearOrgCredentials={vm.clearOrgGoogleCredentials}
      />
      ) : null}

      <AdsAssistantFeedImportFields
        salesGhlOnly={salesGhlOnly}
        embedded={embedded}
        mode={vm.mode}
        setMode={vm.setMode}
        locationId={vm.locationId}
        setLocationId={vm.setLocationId}
        locationsLoaded={vm.locationsLoaded}
        locations={vm.locations}
        pageId={vm.pageId}
        setPageId={vm.setPageId}
        canEditMetaToken={vm.canEditMetaToken}
        overrideToken={vm.overrideToken}
        setOverrideToken={vm.setOverrideToken}
        fetchLimit={vm.fetchLimit}
        setFetchLimit={vm.setFetchLimit}
        daysBack={vm.daysBack}
        setDaysBack={vm.setDaysBack}
        salesMustPickLocation={vm.salesMustPickLocation}
        pasteJson={vm.pasteJson}
        setPasteJson={vm.setPasteJson}
      />

      {vm.mode === "facebook_graph_fetch" ? (
        <p className="mt-2 text-[11px] text-amber-900/90 dark:text-amber-200/90">
          One-time overrides are sent over HTTPS for a single Graph request. The org-wide token is encrypted at rest; set{" "}
          <code className="rounded bg-amber-100/80 px-0.5 dark:bg-amber-950/60">META_FB_PAGE_TOKEN_ENCRYPTION_KEY</code> on
          the server (32-byte hex or base64).
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={vm.busy || !vm.importReady}
          onClick={() => void vm.runImport()}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          {vm.busy
            ? "Importing…"
            : vm.mode === "ghl_social_planner"
              ? "Sync from CRM"
              : vm.mode === "google_gbp_fetch"
                ? "Fetch from Google"
                : "Run import"}
        </button>
        {vm.mode === "ghl_social_planner" ? (
          <button
            type="button"
            disabled={vm.busy}
            onClick={() => void vm.runReconcileLocationTags()}
            className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:bg-zinc-900 dark:text-indigo-100 dark:hover:bg-indigo-950/50"
          >
            {vm.busy ? "Working…" : "Fix location tags"}
          </button>
        ) : null}
      </div>

      {vm.error ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{vm.error}</p> : null}
      {vm.result ? <p className="mt-2 text-xs text-emerald-800 dark:text-emerald-300">{vm.result}</p> : null}
    </section>
  );
}
